<template>
  <div
    :class="['countdown-banner', { 'open': isOpen }]"
    :style="styles"
  >
    <div class="countdown-banner-inner">
      <div class="countdown-banner-content">
        {{ i18n('preTimeLeft') }}
        <span class="time-left">{{ sameDayTimeLeft }}</span>
        {{ i18n('postTimeLeft') }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { TIME } from "@/constants/date-time";

export default {
  name: 'CountdownBanner',
  props: {
    isAvailableToday: {
      type: Boolean,
      required: true
    },
    destinationFloristCutoff: {
      type: String,
      required: true
    },
    displayingCachedData: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      delayHasPassed: false
    }
  },
  computed: {
    ...mapState({
      now: state => state.time.now,
      primaryColor: state => state.primaryColor
    }),
    isOpen() {
      return this.displayingCachedData || this.delayHasPassed;
    },
    sameDayTimeLeft() {
      let timeLeft = this.$tc(this.i18nPath('timeLeft.minutes'), 0);

      if (this.isAvailableToday) {
        const cutoffTimeInMS = new Date(this.destinationFloristCutoff).getTime();
        const minutesRemaining = Math.ceil((cutoffTimeInMS - this.now) / TIME.SECONDS_PER_MINUTE / TIME.MILLISECONDS_PER_SECOND);

        if (minutesRemaining > TIME.MINUTES_PER_HOUR) {
          const hours = Math.floor(minutesRemaining / TIME.MINUTES_PER_HOUR);
          const minutes = minutesRemaining % TIME.MINUTES_PER_HOUR;
          timeLeft = this.i18n('timeLeft.hoursAndMinutes', { hours: hours, minutes: minutes });
        } else {
          timeLeft = this.$tc(this.i18nPath('timeLeft.minutes'), minutesRemaining);
        }
      }

      return timeLeft;
    },
    styles() {
      return {
        color: this.primaryColor
      };
    }
  },
  mounted() {
    setTimeout(this.openCountdownBanner, 200);
  },
  methods: {
    i18n(key, values = null) {
      return this.$t(this.i18nPath(key), values);
    },
    i18nPath(key) {
      return `countdownBanner.${key}`;
    },
    openCountdownBanner() {
      this.delayHasPassed = true;
    }
  }
}
</script>

<style lang="scss">
#core-availability {
  $banner-tri-width: $countdown-banner-width;
  $banner-tri-height: 25px;
  $banner-tri-padding: 7px;
  $banner-background-color: $primary;
  $banner-stroke-color: $white;

  .countdown-banner {
    display: none;
    position: absolute;
    right: $countdown-banner-right-default;
    top: -27px;
    background-color: currentColor;
    color: $banner-background-color;
    width: $banner-tri-width;
    text-decoration: none;
    padding: 0 #{$banner-tri-padding - 1} 0 #{$banner-tri-padding + 1};
    text-align: center;
    height: 12px;
    transition: height 0.4s ease-in 0.2s, padding 0.4s ease-in 0.2s;
    z-index: 1;

    &.no-zip-or-weather {
      right: $countdown-banner-right-no-zip-or-weather;
    }

    @include media-breakpoint-up(lg) {
      right: $countdown-banner-right-lg;
    }

    @include media-breakpoint-up($countdown-banner-visible-breakpoint) {
      display: block;
    }

    &:before {
      content: '';
      display: block;
      width: 0;
      height: 0;
      border-style: solid;
      border-color: transparent transparent currentColor transparent;
      border-width: 0 0 10px 10px;
      position: absolute;
      top: 0;
      right: 100%;
      filter: brightness(70%);
    }

    &:after {
      content: '';
      display: block;
      width: 0;
      height: 0;
      border-style: solid;
      border-color: transparent currentColor transparent;
      border-width: 0 #{$banner-tri-width / 2} #{$banner-tri-height - ($banner-tri-padding / 2) } #{$banner-tri-width / 2};

      position: absolute;
      top: 100%;
      left: 0;

      border-width: 0 86px 14px 86px;
    }

    @include media-breakpoint-between($countdown-banner-visible-breakpoint, $countdown-banner-visible-breakpoint) {
      ~ .row {
        .zip-code {
          p {
            padding-top: 5px;
          }
        }

        .change-zip-code {
          margin-bottom: 10px;
        }
      }
    }

    .countdown-banner-inner {
      display: block;
      border-left: 1px solid $banner-stroke-color;
      border-right: 1px solid $banner-stroke-color;
      border-top: 0 solid $banner-stroke-color;
      position: relative;
      height: 100%;
      padding: 8px 10px 12px;
      font-size: $font-size-12px;
      transition: border-width 0.4s ease-in 0.4s;

      &:before,
      &:after {
        content: '';
        display: block;

        width: 0;
        height: 0;
        border-style: solid;
        border-color: transparent currentColor transparent;
        margin-top: -1px;

        position: absolute;
        left: 0;
        z-index: 5;

        border-width: 0 78px 12px 78px;
        bottom: 5px;
        transition: bottom 0.4s ease-in 0s;
      }


      &:before {
        border-color: transparent $banner-stroke-color transparent;
        margin-top: 0;

        border-width: 0 79px 13px 79px;
        left: -1px;
        bottom: 4px;
      }

      .time-left {
        display: block;
        font-size: 20px;
        font-weight: bold;
      }
    }

    .countdown-banner-content {
      color: $white;
      overflow: hidden;
      height: 0;
      transition: height 0.4s ease-in 0.2s;
    }

    &.open {
      padding-top: #{$banner-tri-padding - 1};
      height: 93px;

      .countdown-banner-inner {
        border-top-width: 1px;

        &:after {
          bottom: -5px;
        }

        &:before {
          bottom: -6px;
        }
      }

      .countdown-banner-content {
        height: 66px;
      }
    }
  }
}
</style>
